import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegimenPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [regimens, setRegimens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userRes, regimensRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/regimens`, { withCredentials: true })
      ]);
      setUser(userRes.data);
      setRegimens(regimensRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="regimen-page">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-btn"
              onClick={() => navigate(user?.role === 'doctor' ? '/doctor-dashboard' : '/patient-dashboard')}
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-primary" />
              <h1 className="font-heading text-xl font-semibold text-text-primary">
                {user?.role === 'doctor' ? 'Patient Regimens' : 'My Health Regimen'}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {regimens.length === 0 ? (
          <div className="card-default text-center py-12">
            <FileText className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary font-body text-lg">No health regimens found</p>
            {user?.role === 'patient' && (
              <p className="text-text-muted font-body text-sm mt-2">
                Your doctor will create a personalized regimen for you
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {regimens.map((regimen) => (
              <div key={regimen.regimen_id} className="card-default" data-testid={`regimen-${regimen.regimen_id}`}>
                <h2 className="font-heading text-2xl font-semibold text-text-primary mb-3">{regimen.title}</h2>
                <p className="text-text-secondary font-body mb-6">{regimen.description}</p>

                {/* Medications */}
                <div className="mb-6">
                  <h3 className="font-body font-semibold text-text-primary mb-3">Medications</h3>
                  <div className="space-y-3">
                    {regimen.medications.map((med, index) => (
                      <div key={index} className="bg-background-subtle rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-body font-medium text-text-primary">{med.name}</p>
                            <p className="text-text-secondary font-body text-sm mt-1">{med.dosage}</p>
                          </div>
                          <span className="text-text-muted font-body text-sm">{med.frequency}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <h3 className="font-body font-semibold text-text-primary mb-2">Instructions</h3>
                  <p className="text-text-secondary font-body whitespace-pre-wrap">{regimen.instructions}</p>
                </div>

                <div className="mt-4 pt-4 border-t border-stone-100">
                  <p className="text-text-muted font-body text-sm">
                    Last updated: {new Date(regimen.updated_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}